import { brandInterpolation } from '@avexa/config/brand'
import i18n, { type ParseKeys } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import en from './locales/en.json'
import nb from './locales/nb.json'

/**
 * Norwegian Bokmål is the primary user-facing language; English is the
 * fallback (Docs/06 §10). A missing `nb` value renders the English string
 * rather than the raw key, and — once the admin Translation Manager exists —
 * gets reported there.
 */

/**
 * Every key that exists in the English catalogue. Data rows carry one of these
 * instead of a display string, so a stale key is a compile error.
 */
export type TranslationKey = ParseKeys

export const SUPPORTED_LANGUAGES = ['nb', 'en'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const DEFAULT_LANGUAGE: SupportedLanguage = 'nb'
export const LANGUAGE_STORAGE_KEY = 'avexa.language'

/** `nb-NO`, `nb`, `no` → `nb`. Anything else → `en`. */
export const normalizeLanguage = (value: string | undefined): SupportedLanguage =>
  value && /^(nb|nn|no)\b/i.test(value) ? 'nb' : 'en'

/** BCP-47 tag for `Intl`, `hreflang` and the `lang` attribute. */
export const languageTag = (language: string): string =>
  normalizeLanguage(language) === 'nb' ? 'nb-NO' : 'en'

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      nb: { translation: nb },
      en: { translation: en },
    },
    supportedLngs: [...SUPPORTED_LANGUAGES],
    fallbackLng: 'en',
    load: 'languageOnly',
    detection: {
      /*
       * 🔴 `querystring` IS FIRST, AND IT WAS MISSING UNTIL 12 OCTOBER.
       *
       * `useDocumentMeta` publishes `<link rel="alternate" hreflang="en"
       * href="https://proffplassen.no/?lang=en">` on every page — we tell Google
       * that is the English URL. The detector did not read the parameter at all,
       * so that URL served whatever the visitor's browser or last choice said.
       * **A claim to a crawler that the page itself contradicts**, and the same
       * class as the parking page that answered 200 while every auth link was
       * broken: nothing errors, and only reading the result shows it.
       *
       * It also cost a verification run — four Norwegian checks silently
       * measured the English page because `?lang=nb` did nothing.
       *
       * FIRST, ahead of `localStorage`, because an explicit link is a stronger
       * signal than a remembered preference: a Norwegian who sends an English
       * colleague `/?lang=en` means it. `caches: ['localStorage']` then stores
       * it, so one click of the switcher still wins afterwards.
       *
       * ⚠️ `lookupQuerystring` must stay `lang` — it is the parameter name
       * `useDocumentMeta` already advertises, and changing one without the other
       * puts the lie back.
       */
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React already escapes everything it renders.
      escapeValue: false,
      /*
       * THE PRODUCT'S NAME IS A VARIABLE, NOT A WORD IN THE COPY.
       *
       * The client is renaming the product and keeping "Avexa Solutions" as the
       * company (Docs/26-RENAME.md). So the catalogues carry `{{product}}`,
       * `{{productUpper}}`, `{{productFull}}` and `{{company}}`, and this line
       * supplies all four to every `t()` call — no call site passes them, and
       * `useTranslation()` needs no change anywhere.
       *
       * ⚠️ It must stay on THIS init. `defaultVariables` is per-instance, and
       * the lazily-registered page copy in `locales/pages/register.ts` calls
       * `addResourceBundle` on this same instance, so those 392 keys are
       * covered too — but a second `i18next.init()` anywhere would not be.
       */
      defaultVariables: brandInterpolation,
    },
    returnNull: false,
    react: { useSuspense: false },
  })

/** Keeps <html lang> truthful — it drives screen-reader pronunciation and SEO. */
const syncDocumentLanguage = (language: string): void => {
  document.documentElement.lang = normalizeLanguage(language)
}

i18n.on('languageChanged', syncDocumentLanguage)
syncDocumentLanguage(i18n.language || DEFAULT_LANGUAGE)

export default i18n
